import { useEffect, useId, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * TimeField — hour, minutes and AM/PM typed straight in, one bordered box.
 *
 * The native time control this replaces makes the user spin an hour wheel and then a
 * minute wheel, and `TimePicker` (the appointment slot list) offers fifteen-minute slots
 * inside a clinic window, which is the wrong shape for "send this campaign at 9:05 pm".
 * Here the entry is three keystrokes and a toggle: two digits of hour, two of minutes,
 * AM or PM. The hour field advances to the minutes on its own once the hour is
 * unambiguous (any first digit above 1, or a second digit), and a 24-hour entry such as
 * "14" is accepted and flips the period for you.
 *
 * Value contract is `<input type="time">`'s: "HH:MM" in 24-hour, or '' while incomplete.
 * `onChange` fires only when all of hour, minute and period are known, and with '' the
 * moment either number is cleared, so a caller never sees a half-typed time.
 */

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export type Period = 'AM' | 'PM';

export interface TimeParts {
  /** 1-12 as typed, or '' */
  hour: string;
  /** '00'-'59' as typed, or '' */
  minute: string;
  period: Period;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** "HH:MM" (24h) to the three visible parts. Empty parts for anything unparseable. */
export function partsFromTime(value: string | null | undefined): TimeParts {
  const match = value ? HHMM_RE.exec(value) : null;
  if (!match) return { hour: '', minute: '', period: 'AM' };
  const hour24 = Number(match[1]);
  return {
    hour: String(((hour24 + 11) % 12) + 1),
    minute: match[2],
    period: hour24 >= 12 ? 'PM' : 'AM',
  };
}

/** The three parts back to "HH:MM" 24h, or '' while either number is missing. */
export function timeFromParts({ hour, minute, period }: TimeParts): string {
  if (hour === '' || minute === '') return '';
  const h12 = Number(hour);
  const m = Number(minute);
  if (!Number.isInteger(h12) || h12 < 1 || h12 > 12) return '';
  if (!Number.isInteger(m) || m < 0 || m > 59) return '';
  const hour24 = period === 'PM' ? (h12 % 12) + 12 : h12 % 12;
  return `${pad2(hour24)}:${pad2(m)}`;
}

/** "HH:MM" to "h:MM AM" for display. */
export function formatTime12h(value: string | null | undefined): string {
  const parts = partsFromTime(value);
  if (!parts.hour) return '';
  return `${parts.hour}:${parts.minute} ${parts.period}`;
}

export interface TimeFieldProps {
  /** "HH:MM" 24-hour, or '' when empty. */
  value: string;
  onChange: (value: string) => void;
  /**
   * Earliest allowed time, "HH:MM". Used only to grey out an AM/PM period that is
   * already wholly in the past; the caller decides what to do with a time that lands
   * before it (see DateTimePicker).
   */
  min?: string;
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  name?: string;
  /** Accessible name for the hour field when no label is rendered. */
  ariaLabel?: string;
  className?: string;
  wrapperClassName?: string;
}

const SEGMENT_CLASS =
  'w-[2ch] bg-transparent text-center text-sm tabular-nums text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:text-slate-500';

export function TimeField({
  value,
  onChange,
  min,
  label,
  hint,
  error,
  required,
  disabled,
  invalid,
  id,
  name,
  ariaLabel,
  className,
  wrapperClassName,
}: TimeFieldProps) {
  const reactId = useId();
  const inputId = id ?? `time-${reactId}`;
  const errorId = error ? `${inputId}-error` : undefined;
  const hintId = hint && !error ? `${inputId}-hint` : undefined;

  const [parts, setParts] = useState<TimeParts>(() => partsFromTime(value));
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);

  // Adopt an outside change (form reset, record loaded) without clobbering what the
  // user is mid-way through typing: only re-derive when the committed value differs
  // from what these parts already express.
  const committedRef = useRef(value);
  useEffect(() => {
    if (committedRef.current !== value) {
      committedRef.current = value;
      setParts(partsFromTime(value));
    }
  }, [value]);

  function commit(next: TimeParts) {
    setParts(next);
    const time = timeFromParts(next);
    if (time !== committedRef.current) {
      committedRef.current = time;
      onChange(time);
    }
  }

  function handleHourChange(raw: string) {
    // A typed "a"/"p" is the fastest way to set the period; honour it wherever it lands.
    const periodKey = /[ap]/i.exec(raw)?.[0];
    const digits = raw.replace(/\D/g, '').slice(-2);
    let next: TimeParts = { ...parts, hour: digits };
    if (periodKey) next.period = periodKey.toLowerCase() === 'p' ? 'PM' : 'AM';

    let advance = false;
    if (digits.length === 2) {
      const n = Number(digits);
      if (n === 0) {
        next = { ...next, hour: '12', period: 'AM' };
      } else if (n > 12 && n <= 23) {
        // 24-hour entry: fold it into the 12-hour display and flip the period.
        next = { ...next, hour: String(n - 12), period: 'PM' };
      } else if (n > 23) {
        // "25" can only have meant 2 then 5: keep the 2 as the hour, hand 5 to minutes.
        next = { ...next, hour: digits[0], minute: digits[1] };
        commit(next);
        minuteRef.current?.focus();
        minuteRef.current?.setSelectionRange(1, 1);
        return;
      }
      advance = true;
    } else if (digits.length === 1 && Number(digits) >= 2) {
      // No hour starts with 2-9 and has a second digit, so it is complete already.
      advance = true;
    }
    commit(next);
    if (advance) {
      minuteRef.current?.focus();
      minuteRef.current?.select();
    }
  }

  function handleMinuteChange(raw: string) {
    const periodKey = /[ap]/i.exec(raw)?.[0];
    let digits = raw.replace(/\D/g, '').slice(-2);
    // A first digit of 6-9 can only be a single-digit minute.
    if (digits.length === 1 && Number(digits) >= 6) digits = `0${digits}`;
    const next: TimeParts = { ...parts, minute: digits };
    if (periodKey) next.period = periodKey.toLowerCase() === 'p' ? 'PM' : 'AM';
    commit(next);
  }

  function handleMinuteKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && parts.minute === '') {
      event.preventDefault();
      hourRef.current?.focus();
    }
  }

  function handleBlur() {
    // Pad a single-digit minute on the way out, so "9:5 PM" reads "9:05 PM".
    if (parts.minute.length === 1) commit({ ...parts, minute: `0${parts.minute}` });
  }

  const minHour24 = min && HHMM_RE.test(min) ? Number(min.slice(0, 2)) : null;
  const amUnavailable = minHour24 != null && minHour24 >= 12;
  const showError = !!error || invalid;

  // Once the whole morning is behind the floor, AM is not a choice: show PM and lock
  // the toggle there rather than offer a period that can only produce a past time.
  useEffect(() => {
    if (amUnavailable && parts.period === 'AM') commit({ ...parts, period: 'PM' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amUnavailable, parts.period]);

  const otherPeriod: Period = parts.period === 'AM' ? 'PM' : 'AM';
  const toggleLocked = otherPeriod === 'AM' && amUnavailable;

  function togglePeriod() {
    if (disabled || toggleLocked) return;
    commit({ ...parts, period: otherPeriod });
  }

  return (
    <div className={cn('w-full', wrapperClassName)}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && (
            <span className="ml-0.5 text-red-500" aria-hidden="true">
              {' *'}
            </span>
          )}
        </label>
      )}

      <div
        data-testid="time-field"
        role="group"
        aria-label={label ? undefined : ariaLabel ?? 'Time'}
        className={cn(
          'flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-2 transition-colors',
          'focus-within:border-primary focus-within:ring-1 focus-within:ring-primary',
          showError && 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500',
          disabled && 'cursor-not-allowed bg-slate-50',
          className,
        )}
      >
        <Clock className="mr-1 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
        <input
          ref={hourRef}
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={parts.hour}
          onChange={(e) => handleHourChange(e.target.value)}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          required={required}
          placeholder="hh"
          aria-label={label ? undefined : `${ariaLabel ?? 'Time'} hour`}
          aria-invalid={showError || undefined}
          aria-describedby={errorId ?? hintId}
          className={SEGMENT_CLASS}
        />
        <span className="text-sm text-slate-400" aria-hidden="true">
          :
        </span>
        <input
          ref={minuteRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={parts.minute}
          onChange={(e) => handleMinuteChange(e.target.value)}
          onKeyDown={handleMinuteKeyDown}
          onFocus={(e) => e.target.select()}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          placeholder="mm"
          aria-label="Minutes"
          aria-invalid={showError || undefined}
          className={SEGMENT_CLASS}
        />
        {/* One toggle showing the current period: click flips AM to PM and back. */}
        <button
          type="button"
          onClick={togglePeriod}
          disabled={disabled || toggleLocked}
          title={
            toggleLocked
              ? 'AM has already passed today'
              : `${parts.period} — click to switch to ${otherPeriod}`
          }
          className={cn(
            'ml-1 w-9 rounded border border-primary bg-white px-1.5 py-0.5 text-center text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            disabled && 'cursor-not-allowed opacity-40 hover:bg-white',
            toggleLocked && !disabled && 'cursor-default hover:bg-white',
          )}
        >
          {parts.period}
        </button>
        {name && <input type="hidden" name={name} value={value} />}
      </div>

      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

TimeField.displayName = 'TimeField';
